import { motion } from 'framer-motion';

const CHAPTERS = [
  {
    n: '01',
    title: 'Quality',
    body: 'At Jakson, quality, technology and design are values that illustrate our commitment to create exceptional experiences. We set our standards extremely high to ensure that every time you turn on a Jakson faucet, you can feel the difference.',
  },
  {
    n: '02',
    title: 'Technology',
    body: 'Single-lever cartridges, HIGHFLOW waterways and concealed diverter systems — engineered in our Bhiwadi works so form never fights function. Everything at Jakson comes with a seal of exceptional quality.',
  },
  {
    n: '03',
    title: 'Design',
    body: 'For us, design is more than just aesthetics — it stands for the perfect synthesis of form and function. The result is the joy you experience every time you use our products.',
  },
];

export const Manifesto = () => (
  <section id="craft" data-testid="manifesto-section" className="relative">
    <div className="relative h-[52vh] sm:h-[64vh] overflow-hidden">
      <motion.img
        src="/assets/img/foundry.jpg"
        alt="Molten brass being cast at the Jakson works"
        data-testid="manifesto-foundry-image"
        initial={{ scale: 1.15 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B] via-transparent to-[#0A0A0B]" />
      <div className="absolute inset-0 flex items-center justify-center px-6">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-serif italic font-light text-3xl sm:text-5xl lg:text-6xl text-center max-w-4xl leading-tight text-[#F5F4F0]"
        >
          “It begins as molten brass —<br />
          <span className="text-gold-gradient">it ends as ritual.”</span>
        </motion.p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
      <p className="kicker mb-4">The Jakson Manifesto</p>
      <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light mb-20">
        Three words we<br />
        <span className="italic text-gold-gradient">build by.</span>
      </h2>

      <div className="space-y-0">
        {CHAPTERS.map((ch, i) => (
          <motion.div
            key={ch.n}
            data-testid={`manifesto-chapter-${ch.n}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="group grid sm:grid-cols-12 gap-6 py-12 border-t border-[#D4AF37]/15 hover:bg-[#121215]/60 transition-colors duration-500 px-2 sm:px-6"
          >
            <div className="sm:col-span-3">
              <span className="font-mono text-sm text-[#D4AF37] tracking-[0.3em]">{ch.n}.</span>
            </div>
            <div className="sm:col-span-4">
              <h3 className="font-serif text-3xl sm:text-4xl font-light group-hover:text-[#F5E6AD] transition-colors duration-500">
                {ch.title}
              </h3>
            </div>
            <div className="sm:col-span-5">
              <p className="text-zinc-400 font-light leading-relaxed">{ch.body}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4AF37]/15"
        data-testid="manifesto-facts"
      >
        {[
          ['Solid Brass', 'Every body cast, never hollow'],
          ['Eco Friendly', 'Water-saver engineering across ranges'],
          ['Hot & Cold', 'Rated for both, operates smoothly'],
          ['Made in India', 'Hindustan Meta Works, Bhiwadi'],
        ].map(([t, s]) => (
          <div key={t} className="bg-[#0A0A0B] p-7">
            <p className="font-serif text-xl text-[#F5E6AD]">{t}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2 leading-relaxed">{s}</p>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);
